import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../lib/i18n";
import { LangToggle } from "../components/LangToggle";
import { formatApiErrorDetail } from "../lib/api";
import { Wrench, Loader2 } from "lucide-react";

const dest = (role) => (role === "admin" ? "/centrale" : role === "tecnico" ? "/tecnico" : "/");

export default function Register() {
  const { t } = useLang();
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "cliente" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) nav(dest(user.role), { replace: true }); }, [user, nav]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const u = await register(form);
      nav(dest(u.role), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col bg-white px-6 pb-10 pt-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white"><Wrench className="h-5 w-5" /></div>
          <span className="font-display text-xl font-extrabold text-slate-900">ProntoSof<span className="text-orange-500">24</span></span>
        </div>
        <LangToggle />
      </div>
      <h1 className="mt-10 font-display text-3xl font-extrabold text-slate-900">{t("register")}</h1>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">{t("name")}</label>
          <input data-testid="reg-name" required value={form.name} onChange={upd("name")} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:bg-white" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">{t("email")}</label>
          <input data-testid="reg-email" type="email" required value={form.email} onChange={upd("email")} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:bg-white" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">{t("phone")}</label>
          <input data-testid="reg-phone" value={form.phone} onChange={upd("phone")} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:bg-white" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">{t("password")}</label>
          <input data-testid="reg-password" type="password" required value={form.password} onChange={upd("password")} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:bg-white" />
        </div>
        {error && <p data-testid="reg-error" className="text-sm font-medium text-rose-600">{error}</p>}
        <button data-testid="reg-submit" disabled={busy} type="submit" className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 font-bold text-white transition hover:bg-blue-700 active:scale-[0.98] disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("register")}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">{t("have_account")} <Link to="/login" className="font-semibold text-blue-600">{t("login")}</Link></p>
    </div>
  );
}
